import React, { useMemo, useState, useEffect, useRef } from 'react';
import { Bar } from 'react-chartjs-2';
import { Paper, Typography } from '@material-ui/core';
import { makeStyles, useTheme } from '@material-ui/core/styles';
import { Skeleton } from '@material-ui/lab';

const useStyles = makeStyles((theme) => ({
    chartCard: {
        background: theme.palette.type === 'dark'
            ? 'linear-gradient(135deg, #1e3a5f 0%, #1e293b 100%)'
            : 'linear-gradient(135deg, #e3f2fd 0%, #f5f5f5 100%)',
        padding: theme.spacing(3),
        borderRadius: 16,
        border: `1px solid ${theme.palette.type === 'dark' ? '#334155' : '#e0e0e0'}`,
        boxShadow: theme.palette.type === 'dark'
            ? '0 8px 32px rgba(0, 0, 0, 0.3)'
            : '0 4px 20px rgba(0, 0, 0, 0.1)',
        transition: 'all 0.6s ease',
    },
    chartTitle: {
        marginBottom: theme.spacing(3),
        fontSize: '2rem',
        fontWeight: 600,
        color: theme.palette.text.primary,
    },
    noDataText: {
        textAlign: 'center',
        padding: theme.spacing(4),
        color: theme.palette.text.secondary,
        fontSize: '1rem',
    },
}));

function ResponseTimeDistributionChart({ loading, data }) {
    const classes = useStyles();
    const theme = useTheme();
    const chartRef = useRef(null);
    const [isVisible, setIsVisible] = useState(false);
    const [animationCount, setAnimationCount] = useState(0);
    const [chartKey, setChartKey] = useState(0);

    // Intersection Observer to detect when chart is in viewport
    useEffect(() => {
        // Only set up observer if chart hasn't animated 2 times yet
        if (animationCount >= 2) return;

        const observer = new IntersectionObserver(
            ([entry]) => {
                if (entry.isIntersecting && !loading && data && animationCount < 2) {
                    setIsVisible(true);
                    setAnimationCount(prev => prev + 1);
                    // Force chart re-render to trigger animation
                    setChartKey(prev => prev + 1);
                } else if (!entry.isIntersecting) {
                    setIsVisible(false);
                }
            },
            {
                threshold: 0.3,
                rootMargin: '0px'
            }
        );

        if (chartRef.current) {
            observer.observe(chartRef.current);
        }

        return () => {
            if (chartRef.current) {
                observer.unobserve(chartRef.current);
            }
        };
    }, [loading, data, animationCount]);

    // Memoize chart data
    const chartData = useMemo(() => {
        if (!data || !data.response_time_distribution) return null;

        const timeDistribution = data.response_time_distribution || [];
        const labels = timeDistribution.map(item => item.time_range);
        const counts = timeDistribution.map(item => item.count);

        return {
            labels: labels,
            datasets: [{
                label: 'Messages',
                data: counts,
                backgroundColor: 'rgba(139, 92, 246, 0.8)',
                borderColor: '#8b5cf6',
                borderWidth: 2,
                borderRadius: 6,
                barThickness: 40,
            }]
        };
    }, [data]);

    // Memoize chart options
    const chartOptions = useMemo(() => ({
        responsive: true,
        maintainAspectRatio: false,
        legend: {
            display: false
        },
        tooltips: {
            backgroundColor: theme.palette.type === 'dark'
                ? 'rgba(30, 41, 59, 0.95)'
                : 'rgba(255, 255, 255, 0.95)',
            titleFontColor: theme.palette.text.primary,
            bodyFontColor: theme.palette.text.secondary,
            borderColor: theme.palette.type === 'dark' ? '#334155' : '#e5e7eb',
            borderWidth: 1,
            cornerRadius: 12,
            displayColors: true,
            bodySpacing: 6,
            titleSpacing: 8,
            footerSpacing: 6,
            xPadding: 12,
            yPadding: 12,
            callbacks: {
                title: (tooltipItems) => {
                    return `Time Range: ${tooltipItems[0].label}`;
                },
                label: (tooltipItem, chartData) => {
                    const dataIndex = tooltipItem.index;
                    const count = tooltipItem.value;
                    const percentage = data.response_time_distribution[dataIndex]?.percentage || 0;
                    return [
                        `Messages: ${parseInt(count).toLocaleString()}`,
                        `Percentage: ${percentage}%`
                    ];
                }
            }
        },
        scales: {
            xAxes: [{
                gridLines: {
                    display: false
                },
                ticks: {
                    fontColor: theme.palette.text.primary,
                    fontSize: 11,
                    fontStyle: '500',
                    padding: 10,
                    maxRotation: 45,
                    minRotation: 0
                }
            }],
            yAxes: [{
                gridLines: {
                    color: theme.palette.divider || '#EEE',
                    borderDash: [3, 3],
                    drawBorder: false
                },
                ticks: {
                    fontColor: theme.palette.text.primary,
                    fontSize: 12,
                    fontStyle: '500',
                    padding: 10,
                    beginAtZero: true,
                    callback: function (value) {
                        if (value >= 1000) {
                            return (value / 1000).toFixed(1) + 'K';
                        }
                        return value;
                    }
                }
            }]
        },
        animation: {
            duration: animationCount <= 2 && isVisible ? 2000 : 0,
            easing: 'easeInOutCubic',
        }
    }), [theme.palette, data, isVisible, animationCount]);

    // Show skeleton while loading
    if (loading) {
        return (
            <Paper className={classes.chartCard}>
                <Skeleton
                    variant="text"
                    width="60%"
                    height={35}
                    style={{ marginBottom: '24px' }}
                    animation="wave"
                />
                <div style={{ height: '350px', display: 'flex', alignItems: 'flex-end', gap: '8px' }}>
                    {[70, 85, 60, 90, 75, 65].map((height, index) => (
                        <div key={index} style={{ flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'flex-end', height: '100%' }}>
                            <Skeleton
                                variant="rect"
                                height={`${height}%`}
                                style={{
                                    borderRadius: 6,
                                    width: '100%'
                                }}
                                animation="wave"
                            />
                        </div>
                    ))}
                </div>
                <div style={{ marginTop: '16px', display: 'flex', justifyContent: 'space-around' }}>
                    {[1, 2, 3, 4, 5, 6].map(item => (
                        <Skeleton
                            key={item}
                            variant="text"
                            width={50}
                            height={18}
                            animation="wave"
                        />
                    ))}
                </div>
            </Paper>
        );
    }

    // Show no data message
    if (!data || !data.response_time_distribution || data.response_time_distribution.length === 0) {
        return (
            <Paper className={classes.chartCard}>
                <Typography className={classes.noDataText}>
                    No response time distribution data available
                </Typography>
            </Paper>
        );
    }

    // Show chart with animation when data is loaded
    return (
        <Paper
            ref={chartRef}
            className={classes.chartCard}
            style={{
                opacity: animationCount >= 2 ? 1 : (isVisible ? 1 : 0.3),
                transform: animationCount >= 2 ? 'scale(1)' : (isVisible ? 'scale(1)' : 'scale(0.95)'),
            }}
        >
            <Typography className={classes.chartTitle}>
                Response Time Distribution
            </Typography>
            <div style={{ height: '350px' }}>
                {chartData && (
                    <Bar
                        key={chartKey}
                        data={chartData}
                        options={chartOptions}
                    />
                )}
            </div>
        </Paper>
    );
}

export default ResponseTimeDistributionChart;